import React, { useState, useEffect, useCallback } from 'react';
import {
  membersApi, savingsPlansApi, debtPlansApi, spendingGoalsApi,
  householdSettingsApi, categoriesApi, accountsApi, transactionsApi,
} from '../services/api';
import { fmt } from '../utils/format';
import { accountBalance } from '../utils/calculations';
import { IRS_IRA_LIMIT, IRS_IRA_CATCHUP, FICA_RATE, TAX_YEAR } from '../utils/constants';
import SetupChecklist from '../components/SetupChecklist';

const SAVINGS_TYPES = [
  { value: 'RETIREMENT_401K', label: '401k' },
  { value: 'IRA',             label: 'IRA' },
  { value: 'BROKERAGE',       label: 'Brokerage' },
  { value: 'COLLEGE',         label: 'College fund' },
  { value: 'OTHER',           label: 'Other' },
];
const DEBT_TYPES = [
  { value: 'MORTGAGE',     label: 'Mortgage' },
  { value: 'STUDENT_LOAN', label: 'Student loan' },
  { value: 'OTHER',        label: 'Other' },
];

const num  = (v) => v === '' || v == null ? null : parseFloat(v);
const pct  = (v) => v == null ? '' : parseFloat((v * 100).toFixed(2));

function SmallInput({ value, onChange, type = 'number', width = 90, step = '0.01', ...rest }) {
  return (
    <input className="field-input" type={type} step={type === 'number' ? step : undefined}
      style={{ width, fontSize: 12, padding: '3px 6px' }}
      value={value ?? ''} onChange={e => onChange(e.target.value)} {...rest} />
  );
}

function CategorySelect({ value, onChange, categories }) {
  return (
    <select className="field-input" style={{ width: 150, fontSize: 12, padding: '3px 6px' }}
      value={value || ''} onChange={e => onChange(e.target.value || null)}>
      <option value="">— none —</option>
      {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
    </select>
  );
}

/**
 * Links a plan to the account that actually holds its balance. Once linked,
 * the account balance drives the payoff/projection math, so updating it from a
 * statement moves the plan — no more keeping two numbers in sync by hand.
 */
function AccountSelect({ value, onChange, accounts }) {
  return (
    <select className="field-input" style={{ width: 150, fontSize: 12, padding: '3px 6px' }}
      value={value || ''} onChange={e => onChange(e.target.value || null)}>
      <option value="">— not linked —</option>
      {accounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
    </select>
  );
}

export default function SetupTab({ month, onRefresh }) {
  const [members,  setMembers]  = useState([]);
  const [savings,  setSavings]  = useState([]);
  const [debts,    setDebts]    = useState([]);
  const [goals,    setGoals]    = useState([]);
  const [settings, setSettings] = useState(null);
  const [cats,     setCats]     = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [txs,      setTxs]      = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      membersApi.getAll(), savingsPlansApi.getAll(), debtPlansApi.getAll(),
      spendingGoalsApi.getAll(), householdSettingsApi.get(), categoriesApi.getAll(month.id),
      accountsApi.getAll(month.id),
      // Needed to value linked accounts that are set to Live.
      transactionsApi.getAll(month.id),
    ]).then(([m, s, d, g, hs, c, a, t]) => {
      setMembers(m.data); setSavings(s.data); setDebts(d.data);
      setGoals(g.data); setSettings(hs.data); setCats(c.data); setAccounts(a.data);
      setTxs(t.data);
      setLoading(false);
    });
  }, [month.id]);

  useEffect(() => { load(); }, [load]);

  // Accounts a plan can be linked to — the ones that hold a balance worth
  // tracking. Budget/cash accounts are excluded; they're spending, not a plan.
  const linkableAccounts = accounts.filter(a => ['Loan', 'Tracking', 'Asset'].includes(a.accountType));

  /** Linked account's balance, valued as the Accounts tab shows it. */
  const linkedBalance = (name) => {
    if (!name) return null;
    const a = accounts.find(x => x.name === name);
    if (!a) return null;
    return accountBalance(a, txs);
  };

  // Local-edit helpers: mutate row in state, persist the full row on blur
  const editRow = (setter) => (id, field, value) =>
    setter(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  // Surface backend validation errors (e.g. "IRA plans need an owning member")
  // instead of leaving an unhandled promise rejection, then resync from the server.
  const onErr = (e) => { window.alert(e?.response?.data?.error || 'Could not save — check the values.'); load(); };

  const saveMember  = (m) => membersApi.update(m.id, { ...m,
    annualSalary: num(m.annualSalary) ?? 0, deductionPct: num(m.deductionPct) ?? 0,
    retirementDate: m.retirementDate || null }).then(load).catch(onErr);
  const saveSavings = (p) => savingsPlansApi.update(p.id, { ...p,
    balance: num(p.balance) ?? 0, annualRate: num(p.annualRate) ?? 0.07,
    yearlyMax: num(p.yearlyMax),
    targetAmount: num(p.targetAmount), targetGoalPct: num(p.targetGoalPct),
    targetDate: p.targetDate || null, monthlyOverride: null }).then(load).catch(onErr);
  const saveDebt    = (p) => debtPlansApi.update(p.id, { ...p,
    principal: num(p.principal) ?? 0, annualRate: num(p.annualRate) ?? 0,
    baseMonthlyPmt: num(p.baseMonthlyPmt) ?? 0, escrow: num(p.escrow),
    payoffGoalDate: p.payoffGoalDate || null }).then(load).catch(onErr);
  const saveGoal    = (g) => spendingGoalsApi.update(g.id, { ...g,
    monthlyAmount: num(g.monthlyAmount) ?? 0 }).then(load).catch(onErr);

  const editMember  = editRow(setMembers);
  const editSavings = editRow(setSavings);
  const editDebt    = editRow(setDebts);
  const editGoal    = editRow(setGoals);

  const memberName = (id) => members.find(m => m.id === id)?.name || '—';

  if (loading || !settings) return <div className="spinner" />;

  const th = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <>
    <SetupChecklist members={members} accounts={accounts} savings={savings} debts={debts} cats={cats} />
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>

      {/* ── LEFT ─────────────────────────────────────────────── */}
      <div>
        {/* Household members */}
        <div className="card">
          <div className="sec-label">Household Members</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            People who earn income or own savings/debt plans. <strong>Annual salary is the only place
            income is entered</strong> — monthly is salary ÷ 12, and every month's income line follows it,
            so Setup and Strategy can't disagree.
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.5 }}>
            <strong>Ded %</strong> should be <strong>total withholding from your paystub</strong> — income
            tax as well as FICA, i.e. gross minus take-home, divided by gross. New members start at the{' '}
            {(FICA_RATE * 100).toFixed(2)}% FICA floor, which is almost never the whole story: leaving it
            there overstates what you have to spend and the Strategy tab will show no cuts when it should.
          </div>
          <table className="tbl">
            <thead><tr>
              <th style={th}>Name</th><th className="r" style={th}>Annual salary</th>
              <th className="r" style={th}>Gross/mo</th>
              <th className="r" style={th}>Ded %</th><th style={th}>Retirement date</th><th style={{ width: 32 }}></th>
            </tr></thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id}>
                  <td><SmallInput type="text" width={110} value={m.name}
                    onChange={v => editMember(m.id, 'name', v)} onBlur={() => saveMember(m)} /></td>
                  <td className="r"><SmallInput width={110} value={num(m.annualSalary) || Math.round(num(m.monthlyGrossIncome) * 12)}
                    onChange={v => editMember(m.id, 'annualSalary', v)} onBlur={() => saveMember(m)} /></td>
                  {/* Derived, never typed — the spreadsheet does salary/12 too, and
                      one input means the Strategy tab can't disagree with Setup. */}
                  <td className="r muted" style={{ fontSize: 12 }}
                    title="Salary ÷ 12. This is what every month's income line uses.">
                    {fmt((num(m.annualSalary) || num(m.monthlyGrossIncome) * 12) / 12)}
                  </td>
                  <td className="r">
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <SmallInput width={60} step="0.1" value={pct(num(m.deductionPct))}
                        onChange={v => editMember(m.id, 'deductionPct', v === '' ? 0 : parseFloat(v) / 100)}
                        onBlur={() => saveMember(m)} />
                      {/* Still on the bare FICA floor — almost certainly not this
                          person's real withholding, and it silently inflates net
                          income enough to suppress every budget cut. */}
                      {Math.abs((num(m.deductionPct) ?? 0) - FICA_RATE) < 0.0005 && (
                        <span title={'This is the FICA floor only (Social Security + Medicare). '
                          + 'Real withholding usually also includes federal and state income tax. '
                          + 'Use (gross − take-home) ÷ gross from a paystub.'}
                          style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 99,
                            border: '1px solid var(--amber)', background: 'var(--amber-bg)',
                            color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.04em',
                            cursor: 'help', whiteSpace: 'nowrap',
                          }}>
                          FICA only
                        </span>
                      )}
                    </div>
                  </td>
                  <td><SmallInput type="date" width={135} value={m.retirementDate}
                    onChange={v => editMember(m.id, 'retirementDate', v)} onBlur={() => saveMember(m)} /></td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => {
                    if (window.confirm(`Remove ${m.name}? Their plans stay but lose their owner.`))
                      membersApi.delete(m.id).then(load);
                  }}>✕</button></td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr><td colSpan={5} style={{ color: 'var(--text3)', fontSize: 12, padding: '12px 0' }}>
                  Add yourself (and a partner) to get started — names, incomes, and retirement dates drive everything else.
                </td></tr>
              )}
            </tbody>
          </table>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }}
            onClick={() => membersApi.add({ name: `Person ${members.length + 1}`, sortOrder: members.length }).then(load)}>
            + Member
          </button>
        </div>

        {/* Spending goals */}
        <div className="card">
          <div className="sec-label">Variable Spending Goals</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            Monthly targets for flexible spending — these absorb 65% of any shortfall cut in Strategy.
          </div>
          <table className="tbl">
            <thead><tr>
              <th style={th}>Name</th><th className="r" style={th}>$/month</th>
              <th style={th}>Budget category</th><th style={{ width: 32 }}></th>
            </tr></thead>
            <tbody>
              {goals.map(g => (
                <tr key={g.id}>
                  <td><SmallInput type="text" width={130} value={g.name}
                    onChange={v => editGoal(g.id, 'name', v)} onBlur={() => saveGoal(g)} /></td>
                  <td className="r"><SmallInput value={g.monthlyAmount}
                    onChange={v => editGoal(g.id, 'monthlyAmount', v)} onBlur={() => saveGoal(g)} /></td>
                  <td><CategorySelect categories={cats} value={g.linkedCategoryName}
                    onChange={v => { editGoal(g.id, 'linkedCategoryName', v); saveGoal({ ...g, linkedCategoryName: v }); }} /></td>
                  <td><button className="btn btn-danger btn-sm"
                    onClick={() => spendingGoalsApi.delete(g.id).then(load)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }}
            onClick={() => spendingGoalsApi.add({ name: 'New goal', monthlyAmount: 0, sortOrder: goals.length }).then(load)}>
            + Spending goal
          </button>
        </div>

        {/* Household settings */}
        <div className="card">
          <div className="sec-label">Household Settings</div>
          <table className="tbl"><tbody>
            <tr>
              <td style={{ fontSize: 13 }}>Shared retirement goal</td>
              <td className="r"><SmallInput width={120} value={settings.sharedRetirementGoal}
                onChange={v => setSettings(s => ({ ...s, sharedRetirementGoal: v }))}
                onBlur={() => householdSettingsApi.update({ ...settings, sharedRetirementGoal: num(settings.sharedRetirementGoal) ?? 2000000 }).then(load)} /></td>
            </tr>
            <tr>
              <td style={{ fontSize: 13 }}>IRA share of goal (each)</td>
              <td className="r">
                <SmallInput width={70} step="0.1" value={pct(num(settings.iraGoalSharePct))}
                  onChange={v => setSettings(s => ({ ...s, iraGoalSharePct: v === '' ? 0.145 : parseFloat(v) / 100 }))}
                  onBlur={() => householdSettingsApi.update(settings).then(load)} />
                <span style={{ fontSize: 11, marginLeft: 3 }}>%</span>
              </td>
            </tr>
            <tr>
              <td style={{ fontSize: 13 }}>Max 401k (% of own income)</td>
              <td className="r">
                <SmallInput width={70} step="1" value={pct(num(settings.max401kIncomePct))}
                  onChange={v => setSettings(s => ({ ...s, max401kIncomePct: v === '' ? 0.8 : parseFloat(v) / 100 }))}
                  onBlur={() => householdSettingsApi.update(settings).then(load)} />
                <span style={{ fontSize: 11, marginLeft: 3 }}>%</span>
              </td>
            </tr>
          </tbody></table>
        </div>
      </div>

      {/* ── RIGHT ────────────────────────────────────────────── */}
      <div>
        {/* Savings plans */}
        <div className="card">
          <div className="sec-label">Savings &amp; Investment Plans</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            401k/IRA plans need an owner — their income caps and retirement date drive the
            monthly Goal that Strategy shows. That amount is not typed on Strategy.
          </div>
          {savings.map(p => (
            <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <SmallInput type="text" width={150} value={p.name}
                  onChange={v => editSavings(p.id, 'name', v)} onBlur={() => saveSavings(p)} />
                <select className="field-input" style={{ fontSize: 12, padding: '3px 6px' }} value={p.type}
                  onChange={e => {
                    const type = e.target.value;
                    // 401k/IRA require an owning member — default to the first one so the
                    // switch doesn't fail backend validation with a runtime error.
                    let ownerMemberId = p.ownerMemberId;
                    if ((type === 'IRA' || type === 'RETIREMENT_401K') && ownerMemberId == null) {
                      if (members.length === 0) {
                        window.alert('Add a household member first — 401k and IRA plans need an owner.');
                        return;
                      }
                      ownerMemberId = members[0].id;
                      editSavings(p.id, 'ownerMemberId', ownerMemberId);
                    }
                    // Hardwire the IRS IRA limit as the default cap when there isn't one yet.
                    let yearlyMax = p.yearlyMax;
                    if (type === 'IRA' && (yearlyMax == null || yearlyMax === '')) {
                      yearlyMax = IRS_IRA_LIMIT;
                      editSavings(p.id, 'yearlyMax', yearlyMax);
                    }
                    editSavings(p.id, 'type', type);
                    saveSavings({ ...p, type, ownerMemberId, yearlyMax });
                  }}>
                  {SAVINGS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select className="field-input" style={{ fontSize: 12, padding: '3px 6px' }} value={p.ownerMemberId || ''}
                  onChange={e => { const v = e.target.value ? Number(e.target.value) : null;
                    editSavings(p.id, 'ownerMemberId', v); saveSavings({ ...p, ownerMemberId: v }); }}>
                  <option value="">shared / no owner</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }}
                  onClick={() => { if (window.confirm(`Remove ${p.name}?`)) savingsPlansApi.delete(p.id).then(load); }}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, alignItems: 'center' }}>
                {p.linkedAccountName ? (
                  <label title="Balance comes from the linked account — edit it in the Accounts tab">
                    Balance{' '}
                    <span style={{ fontWeight: 600 }}>{fmt(linkedBalance(p.linkedAccountName) ?? 0)}</span>
                    <span style={{ fontSize: 10, color: 'var(--blue)', marginLeft: 5 }}>from account</span>
                  </label>
                ) : (
                  <label>Balance <SmallInput value={p.balance}
                    onChange={v => editSavings(p.id, 'balance', v)} onBlur={() => saveSavings(p)} /></label>
                )}
                <label>Account <AccountSelect accounts={linkableAccounts} value={p.linkedAccountName}
                  onChange={v => { editSavings(p.id, 'linkedAccountName', v);
                    saveSavings({ ...p, linkedAccountName: v }); }} /></label>
                <label>Rate % <SmallInput width={55} step="0.1" value={pct(num(p.annualRate))}
                  onChange={v => editSavings(p.id, 'annualRate', v === '' ? 0.07 : parseFloat(v) / 100)}
                  onBlur={() => saveSavings(p)} /></label>
                {p.type === 'IRA' && (
                  <label title={`IRS ${TAX_YEAR} IRA limit is $${IRS_IRA_LIMIT.toLocaleString()} ($${(IRS_IRA_LIMIT + IRS_IRA_CATCHUP).toLocaleString()} if age 50+). Editable.`}>
                    Yearly max <SmallInput width={70} value={p.yearlyMax} placeholder={String(IRS_IRA_LIMIT)}
                    onChange={v => editSavings(p.id, 'yearlyMax', v)} onBlur={() => saveSavings(p)} /></label>
                )}
                {p.type === 'BROKERAGE' && (
                  <label title="Share of the shared retirement goal this brokerage funds (defaults to the IRA share)">
                    Share % of goal <SmallInput width={55} step="1" value={pct(num(p.targetGoalPct))}
                    onChange={v => editSavings(p.id, 'targetGoalPct', v === '' ? null : parseFloat(v) / 100)}
                    onBlur={() => saveSavings(p)} /></label>
                )}
                {(p.type === 'COLLEGE' || p.type === 'OTHER') && (
                  <>
                    <label>Target $ <SmallInput width={85} value={p.targetAmount}
                      onChange={v => editSavings(p.id, 'targetAmount', v)} onBlur={() => saveSavings(p)} /></label>
                    <label>Share % <SmallInput width={55} step="1" value={pct(num(p.targetGoalPct))}
                      onChange={v => editSavings(p.id, 'targetGoalPct', v === '' ? null : parseFloat(v) / 100)}
                      onBlur={() => saveSavings(p)} /></label>
                    <label>By <SmallInput type="date" width={130} value={p.targetDate}
                      onChange={v => editSavings(p.id, 'targetDate', v)} onBlur={() => saveSavings(p)} /></label>
                  </>
                )}
                <label>Category <CategorySelect categories={cats} value={p.linkedCategoryName}
                  onChange={v => { editSavings(p.id, 'linkedCategoryName', v); saveSavings({ ...p, linkedCategoryName: v }); }} /></label>
              </div>
            </div>
          ))}
          <button className="btn btn-primary btn-sm"
            onClick={() => savingsPlansApi.add({
              name: 'New savings plan', type: 'BROKERAGE', sortOrder: savings.length,
            }).then(load)}>
            + Savings plan
          </button>
        </div>

        {/* Debt plans */}
        <div className="card">
          <div className="sec-label">Debt Payoff Plans</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            Set a payoff date to compute the extra principal needed each month.
            "Toggle full payment" controls whether the Strategy switch moves the whole
            payment (typical for student loans) or just the extra (typical for mortgages).
          </div>
          {debts.map(p => (
            <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <SmallInput type="text" width={150} value={p.name}
                  onChange={v => editDebt(p.id, 'name', v)} onBlur={() => saveDebt(p)} />
                <select className="field-input" style={{ fontSize: 12, padding: '3px 6px' }} value={p.type}
                  onChange={e => { editDebt(p.id, 'type', e.target.value); saveDebt({ ...p, type: e.target.value }); }}>
                  {DEBT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select className="field-input" style={{ fontSize: 12, padding: '3px 6px' }} value={p.ownerMemberId || ''}
                  onChange={e => { const v = e.target.value ? Number(e.target.value) : null;
                    editDebt(p.id, 'ownerMemberId', v); saveDebt({ ...p, ownerMemberId: v }); }}>
                  <option value="">shared</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }}
                  onClick={() => { if (window.confirm(`Remove ${p.name}?`)) debtPlansApi.delete(p.id).then(load); }}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, alignItems: 'center' }}>
                {p.linkedAccountName ? (
                  <label title="Principal comes from the linked account — edit it in the Accounts tab">
                    Principal{' '}
                    <span style={{ fontWeight: 600 }}>{fmt(Math.abs(linkedBalance(p.linkedAccountName) ?? 0))}</span>
                    <span style={{ fontSize: 10, color: 'var(--blue)', marginLeft: 5 }}>from account</span>
                  </label>
                ) : (
                  <label>Principal <SmallInput value={p.principal}
                    onChange={v => editDebt(p.id, 'principal', v)} onBlur={() => saveDebt(p)} /></label>
                )}
                <label>Account <AccountSelect accounts={linkableAccounts} value={p.linkedAccountName}
                  onChange={v => { editDebt(p.id, 'linkedAccountName', v);
                    saveDebt({ ...p, linkedAccountName: v }); }} /></label>
                <label>Rate % <SmallInput width={55} step="0.05" value={pct(num(p.annualRate))}
                  onChange={v => editDebt(p.id, 'annualRate', v === '' ? 0 : parseFloat(v) / 100)}
                  onBlur={() => saveDebt(p)} /></label>
                <label>Base pmt <SmallInput width={75} value={p.baseMonthlyPmt}
                  onChange={v => editDebt(p.id, 'baseMonthlyPmt', v)} onBlur={() => saveDebt(p)} /></label>
                {p.type === 'MORTGAGE' && (
                  <label>Escrow <SmallInput width={70} value={p.escrow}
                    onChange={v => editDebt(p.id, 'escrow', v)} onBlur={() => saveDebt(p)} /></label>
                )}
                <label>Payoff by <SmallInput type="date" width={130} value={p.payoffGoalDate}
                  onChange={v => editDebt(p.id, 'payoffGoalDate', v)} onBlur={() => saveDebt(p)} /></label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="checkbox" checked={!!p.toggleFullPayment}
                    onChange={e => { editDebt(p.id, 'toggleFullPayment', e.target.checked); saveDebt({ ...p, toggleFullPayment: e.target.checked }); }} />
                  toggle full payment
                </label>
                <label>Category <CategorySelect categories={cats} value={p.linkedCategoryName}
                  onChange={v => { editDebt(p.id, 'linkedCategoryName', v); saveDebt({ ...p, linkedCategoryName: v }); }} /></label>
              </div>
              {(() => {
                const owed = Math.abs(linkedBalance(p.linkedAccountName) ?? p.principal ?? 0);
                return owed > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                    Balance owed: {fmt(-owed)}
                  </div>
                );
              })()}
            </div>
          ))}
          <button className="btn btn-primary btn-sm"
            onClick={() => debtPlansApi.add({
              name: 'New debt', type: 'OTHER', sortOrder: debts.length,
            }).then(load)}>
            + Debt plan
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
